import { z } from "zod";
import { ProposedEncounterReferralSchema } from "./agent.js";
import { IdSchema } from "./primitives.js";
import { ReferralEnclosureSelectionSchema } from "./referral.js";
export const ENCOUNTER_REFERRAL_DRAFT_PROMPT_VERSION = "encounter-referral-draft-v5";
/**
 * The previous prompt's responses still parse for one release: the web app and
 * agent-api deploy separately, so either may briefly meet the other's older build.
 */
const ACCEPTED_ENCOUNTER_REFERRAL_DRAFT_PROMPT_VERSIONS = [
    "encounter-referral-draft-v4",
    ENCOUNTER_REFERRAL_DRAFT_PROMPT_VERSION,
];
/** Clinician instructions supplement the saved encounter; they are never a substitute for scope. */
export const EncounterReferralDraftRequestSchema = z
    .object({
    siteId: IdSchema,
    patientId: IdSchema,
    expectedEncounterVersion: z.number().int().nonnegative(),
    instruction: z.string().trim().min(1).max(8_000).optional(),
    proposedReferral: z
        .object({
        summary: z.string().trim().min(1).max(500).optional(),
        referralQuestion: z.string().trim().min(1).max(4_000).optional(),
        clinicalRationale: z.string().trim().min(1).max(8_000).optional(),
        specialtyCode: z.string().trim().min(1).max(80).optional(),
        urgency: z.enum(["routine", "urgent", "emergent", "soon"]).optional(),
        diagnosisCodes: z.array(z.string().trim().min(1).max(80)).max(20).optional(),
    })
        .strict()
        .optional(),
})
    .strict();
/** A partial clinical draft survives clarification without inventing diagnoses or a specialty. */
export const EncounterReferralDraftContentSchema = z
    .object({
    specialtyCode: z.string().trim().min(1).max(80).nullable(),
    summary: z.string().trim().max(500),
    referralQuestion: z.string().trim().max(4_000),
    clinicalRationale: z.string().trim().max(8_000),
    urgency: z.enum(["routine", "urgent", "emergent"]).nullable(),
    diagnosisCodes: z.array(z.string().trim().min(1).max(80)).max(20),
    enclosures: z.array(ReferralEnclosureSelectionSchema).max(50).optional(),
    /**
     * Drafted "relevant history" points about the referred problem, each traced
     * to the record. Absent when none were drafted; the governed letter then
     * builds its history from the note (REF-2).
     */
    relevantHistory: z.array(z.string().trim().min(1).max(2_000)).max(100).optional(),
})
    .strict();
const metadata = {
    model: z.string().min(1),
    promptVersion: z.enum(ACCEPTED_ENCOUNTER_REFERRAL_DRAFT_PROMPT_VERSIONS),
    encounterVersion: z.number().int().nonnegative(),
    /** Clinical suggestion for review; this label never authorizes a routing code or destination. */
    suggestedSpecialty: z.string().trim().min(1).max(200).optional(),
    /**
     * Physician-facing notes about statements the draft could not trace to a
     * recorded source. The draft is still returned: the physician's review and
     * approval is the gate, and an untraced statement is corrected there, not
     * hidden. Present only when something needs a second look.
     */
    reviewNotes: z.array(z.string().trim().min(1).max(500)).max(10).optional(),
};
export const EncounterReferralDraftResponseSchema = z.discriminatedUnion("kind", [
    z
        .object({
        kind: z.literal("proposal"),
        proposal: ProposedEncounterReferralSchema.extend({
            specialtyCode: z.string().trim().min(1).max(80),
        }),
        ...metadata,
    })
        .strict(),
    z
        .object({
        kind: z.literal("clarification"),
        question: z.string().trim().min(1).max(1_000),
        draft: EncounterReferralDraftContentSchema.optional(),
        ...metadata,
    })
        .strict(),
]);
//# sourceMappingURL=encounter-referral-draft.js.map